import angular from 'angular';

class ActivitiesParser {
  constructor($sce) {
    'ngInject';

    this.$sce = $sce;
    this._expressions = {
      tags: /<(\w+)(.*?)>((?:(?![<|>]).)*)<\/\w+>/ig,
      attrs: /data-(\w+)=['|"](.*?)(?:['|"](?:[ |>]|$))/ig,
      quotes: /['|"]/g,
      wrappingQuotes: /^(['|"])(.*?)(['|"])$/g,
    };
    this._activityClass = 'alActivity';
    this._detailClassesInfo = {
      component: 'alActivityDetail',
      elements: {
        element: 'element',
        detail: 'detail',
        label: 'label',
        detailList: {
          main: 'detailList',
          title: 'title',
          list: 'list',
        },
      },
    };
    this._detailAttrsInfo = {
      dataAttribute: {
        html: 'data-activity-detail',
        dataset: 'activityDetail',
      },
      elements: {
        element: 'element',
        detail: 'detail',
        label: 'label',
      },
    };
  }
  parse(activities, user) {
    return angular.isArray(activities) ?
      activities.map((activity) => this._parse(activity, user)) :
      this._parse(activities, user);
  }
  getDetailFromElement(element) {
    const {
      dataAttribute: {
        html: dataAttr,
        dataset: dataProp,
      },
      elements: elementAttrs,
    } = this._detailAttrsInfo;
    let result = null;
    if (
      element &&
      element.dataset &&
      element.dataset[dataProp] === elementAttrs.label
    ) {
      const detailElementTag = this._findDetailElement(element);
      if (detailElementTag) {
        const detail = detailElementTag.querySelector(`[${dataAttr}="${elementAttrs.detail}"]`);
        if (detail) {
          result = detail.innerHTML;
        }
      }
    }

    return result;
  }
  _findDetailElement(element) {
    const {
      dataAttribute: {
        dataset: dataProp,
      },
      elements: elementAttrs,
    } = this._detailAttrsInfo;

    let currentElement = element;
    let done = false;
    while (!done) {
      if (!currentElement) {
        done = true;
      } else if (
        currentElement &&
        currentElement.dataset &&
        currentElement.dataset[dataProp] === elementAttrs.element
      ) {
        done = true;
      }

      if (!done) {
        currentElement = currentElement.parentElement;
      }
    }

    return currentElement;
  }
  _parse(activity, user) {
    const entities = this._extractEntities(activity);
    const tags = this._extractTags(activity);
    let summaryHTML = activity.summary;

    tags.forEach((tag) => {
      if (tag.attrs.entity) {
        const entity = tag.attrs.entity.toLowerCase();
        let newTag;
        switch (entity) {
        case 'permission':
          newTag = this._formatPermissionTag(tag, entities, activity, user);
          break;
        case 'person':
          newTag = this._formatPersonTag(tag, entities, activity, user);
          break;
        case 'organization':
          newTag = this._formatOrganizationTag(tag, entities, activity, user);
          break;
        case 'application':
          newTag = this._formatApplicationTag(tag, entities, activity, user);
          break;
        case 'product':
          newTag = this._formatProductTag(tag, entities, activity, user);
          break;
        default:
          newTag = '';
        }

        if (newTag) {
          summaryHTML = summaryHTML.replace(tag.tag, newTag);
        }
      }
    });

    summaryHTML = `<div class="${this._activityClass}">${summaryHTML}</div>`;

    return Object.assign(
      {},
      activity,
      {
        summaryHTML: this.$sce.trustAsHtml(summaryHTML),
      },
    );
  }
  _formatPermissionTag(tag, entities) {
    return this._formatTagWithDetail(tag, entities.permission, 'permission', 'permissions');
  }
  _formatPersonTag(tag, entities) {
    return this._formatTagWithDetailIfNeeded(tag, entities.person, 'people');
  }
  _formatOrganizationTag(tag, entities) {
    return this._formatTagWithDetailIfNeeded(tag, entities.organization, 'organization');
  }
  _formatApplicationTag(tag, entities) {
    return this._formatTagWithDetailIfNeeded(tag, entities.application, 'application');
  }
  _formatProductTag(tag, entities) {
    return this._formatTagWithDetailIfNeeded(tag, entities.product, 'product');
  }
  _formatTagWithDetail(tag, entities, singular, plural) {
    const listItems = tag.attrs.value.map((item) => entities[item].name);
    const howManyItems = tag.attrs.value.length;
    const word = howManyItems === 1 ? singular : plural;
    const list = this._getDetailList(plural, listItems);
    const label = `${howManyItems} ${word}`;
    return this._createDetail(list, label);
  }
  _formatTagWithDetailIfNeeded(tag, entities, plural) {
    let code;
    const howManyItems = tag.attrs.value.length;
    if (howManyItems > 1) {
      const listItems = tag.attrs.value.map((item) => entities[item].name);
      const list = this._getDetailList(plural, listItems);
      const label = `${howManyItems} ${plural}`;
      code = this._createDetail(list, label);
    } else {
      const [firstId] = tag.attrs.value;
      const entity = entities[firstId];
      code = `<strong>${entity.name}</strong>`;
    }

    return code;
  }
  _getDetailList(title, items) {
    const {
      component: componentClass,
      elements: {
        detailList: elements,
      },
    } = this._detailClassesInfo;

    const component = `${componentClass}_${elements.main}`;
    const lis = items
    .map((item) => `<li>${item}</li>`)
    .join('\n');

    return `
      <div class="${component}">
        <div class="${component}_${elements.title}">${title}</div>
        <ul class="${component}_${elements.list}">
          ${lis}
        </ul>
      </div>
    `;
  }
  _createDetail(contents, label, options = {}) {
    const {
      component,
      elements: elementClasses,
    } = this._detailClassesInfo;
    const {
      dataAttribute: {
        html: dataAttr,
      },
      elements: elementAttrs,
    } = this._detailAttrsInfo;

    const elementTagAttributes = this._createDetailTagAttributes(
      [`${component}_${elementClasses.element}`],
      {
        [dataAttr]: elementAttrs.element,
      },
      options.elementTagClass,
      options.elementTagAttributes,
    );

    const detailTagAttributes = this._createDetailTagAttributes(
      [`${component}_${elementClasses.detail}`],
      {
        [dataAttr]: elementAttrs.detail,
      },
      options.detailTagClass,
      options.detailTagAttributes,
    );

    let labelTag;
    if (label.match(/<\/w+>/)) {
      labelTag = label;
    } else {
      const labelTagAttributes = this._createDetailTagAttributes(
        [`${component}_${elementClasses.label}`],
        {
          [dataAttr]: elementAttrs.label,
        },
        options.labelTagClass,
        options.labelTagAttributes,
      );

      labelTag = `<a ${labelTagAttributes}>${label}</a>`;
    }

    return `
      <span ${elementTagAttributes}>
        ${labelTag}
        <span ${detailTagAttributes}>
          ${contents}
        </span>
      </span>
    `;
  }
  _createDetailTagAttributes(classes, attributes, customClasses = null, customAttributes = null) {
    const tagClasses = classes.slice();
    if (customClasses) {
      const customTagClasses = angular.isArray(customClasses) ?
        customClasses :
        [customClasses];
      tagClasses.push(...customTagClasses);
    }

    const attrs = Object.assign(
      {
        class: tagClasses.join(' '),
      },
      attributes,
    );

    if (customAttributes) {
      Object.keys(customAttributes).forEach((attrName) => {
        if (attrName !== 'class') {
          attrs[attrName] = customAttributes[attrName];
        }
      });
    }

    return Object.keys(attrs)
    .map((attrName) => `${attrName}="${attrs[attrName]}"`)
    .join(' ')
    .trim();
  }
  _extractTags(activity) {
    const { summary } = activity;
    const result = [];
    let match = this._expressions.tags.exec(summary);
    while (match) {
      const [tag, tagName, attrsStr, contents] = match;
      result.push({
        tag,
        tagName,
        attrs: this._parseTagAttributes(attrsStr),
        contents: contents.trim().replace(this._expressions.quotes, ''),
      });

      match = this._expressions.tags.exec(summary);
    }

    return result;
  }
  _formatTagValueAttribute(text) {
    return text
    .split(',')
    .map((item) => (
      item
      .trim()
      .replace(this._expressions.wrappingQuotes, '$2')
      .trim()
    ));
  }
  _parseTagAttributes(attributes) {
    let result = {};
    let match = this._expressions.attrs.exec(attributes);
    while (match) {
      const [, name, value] = match;
      result[name.trim()] = value.trim();
      match = this._expressions.attrs.exec(attributes);
    }

    result = this._hotFixAttributes(result);

    if (result.value) {
      result.value = this._formatTagValueAttribute(result.value);
    }

    return result;
  }
  _hotFixAttributes(attributes) {
    const attrs = Object.assign({}, attributes);
    if (
      attrs.entity &&
      attrs.value &&
      !attrs.entity.match(/^\w+$/)
    ) {
      const { value, entity } = attrs;
      attrs.entity = value;
      attrs.value = entity;
    }

    if (attrs.entity === 'persons') {
      attrs.entity = 'person';
    }

    if (attrs.entity === 'permissions') {
      attrs.entity = 'permission';
    }

    return attrs;
  }
  _extractEntities(activity) {
    const {
      actor,
      origin,
      audience,
      object,
      target,
    } = activity;
    const entities = [actor];
    if (origin) {
      entities.push(origin);
    }

    if (audience) {
      entities.push(audience);
    }

    if (object) {
      if (object.type.toLowerCase() === 'collection') {
        entities.push(...object.items);
      } else {
        entities.push(object);
      }
    }

    if (target) {
      entities.push(target);
    }

    const result = {};
    entities.forEach((entity) => {
      const type = entity.type.toLowerCase();
      if (!result[type]) {
        result[type] = {};
      }

      result[type][entity.id] = entity;
    });

    return result;
  }
}

export default ActivitiesParser;
